import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { qk } from './queryKeys';
import type { Guid, MyTeamOfferDto, TeamCardDto, TeamDistributionDto, TeamOfferDto, TeamPanelDto, TeamWorkItemDto } from './types';

export const teamApi = {
  panel: (teamId: Guid) => api<TeamPanelDto>(`/api/communities/${teamId}/panel`),
  assign: (teamId: Guid, body: { assignmentIds: Guid[]; userId: Guid; note: string | null }) =>
    api<TeamDistributionDto>(`/api/communities/${teamId}/work/assign`, { method: 'POST', body }),
  distribute: (teamId: Guid, body: { assignmentIds: Guid[]; userIds: Guid[]; apply: boolean }) =>
    api<TeamDistributionDto>(`/api/communities/${teamId}/work/distribute`, { method: 'POST', body }),
  note: (assignmentId: Guid, note: string | null) => api<TeamWorkItemDto>(`/api/assignments/${assignmentId}/team-note`, { method: 'PUT', body: { note } }),
  offers: (teamId: Guid) => api<TeamOfferDto[]>(`/api/communities/${teamId}/offers`),
  sendOffer: (teamId: Guid, userId: Guid, message: string | null) =>
    api<TeamOfferDto>(`/api/communities/${teamId}/offers`, { method: 'POST', body: { userId, message } }),
  withdrawOffer: (teamId: Guid, offerId: Guid) => api<void>(`/api/communities/${teamId}/offers/${offerId}`, { method: 'DELETE' }),
  myOffers: () => api<MyTeamOfferDto[]>('/api/my/team-offers'),
  answer: (offerId: Guid, accept: boolean) => api<TeamCardDto>(`/api/team-offers/${offerId}/${accept ? 'accept' : 'decline'}`, { method: 'POST' }),
};

export function useTeamPanel(teamId: Guid | undefined) {
  return useQuery({ queryKey: qk.team.panel(teamId ?? ''), queryFn: () => teamApi.panel(teamId!), enabled: !!teamId, retry: false });
}

/** After work moves: the work list, the panel numbers, and the lead's own assignments. */
function useInvalidateWork(teamId: Guid) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.market.teamWork(teamId) });
    void qc.invalidateQueries({ queryKey: qk.team.panel(teamId) });
    void qc.invalidateQueries({ queryKey: qk.my.assignments });
  };
}

export function useAssignWork(teamId: Guid) {
  const invalidate = useInvalidateWork(teamId);
  return useMutation({
    mutationFn: (body: { assignmentIds: Guid[]; userId: Guid; note: string | null }) => teamApi.assign(teamId, body),
    onSuccess: invalidate,
  });
}

export function useDistributeWork(teamId: Guid) {
  const invalidate = useInvalidateWork(teamId);
  return useMutation({
    mutationFn: (body: { assignmentIds: Guid[]; userIds: Guid[]; apply: boolean }) => teamApi.distribute(teamId, body),
    onSuccess: (r) => { if (r.applied) invalidate(); },
  });
}

export function useTeamNote(teamId: Guid) {
  const invalidate = useInvalidateWork(teamId);
  return useMutation({ mutationFn: ({ assignmentId, note }: { assignmentId: Guid; note: string | null }) => teamApi.note(assignmentId, note), onSuccess: invalidate });
}

export function useTeamOffers(teamId: Guid | undefined) {
  return useQuery({ queryKey: qk.team.offers(teamId ?? ''), queryFn: () => teamApi.offers(teamId!), enabled: !!teamId });
}

export function useSendOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, userId, message }: { teamId: Guid; userId: Guid; message: string | null }) => teamApi.sendOffer(teamId, userId, message),
    onSuccess: (_, v) => {
      void qc.invalidateQueries({ queryKey: qk.team.offers(v.teamId) });
      void qc.invalidateQueries({ queryKey: qk.team.panel(v.teamId) });
    },
  });
}

export function useWithdrawOffer(teamId: Guid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (offerId: Guid) => teamApi.withdrawOffer(teamId, offerId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.team.offers(teamId) });
      void qc.invalidateQueries({ queryKey: qk.team.panel(teamId) });
    },
  });
}

export function useMyTeamOffers(enabled = true) {
  return useQuery({ queryKey: qk.team.myOffers, queryFn: teamApi.myOffers, enabled });
}

export function useAnswerOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ offerId, accept }: { offerId: Guid; accept: boolean }) => teamApi.answer(offerId, accept),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.team.myOffers });
      void qc.invalidateQueries({ queryKey: ['market', 'teams'] });
      void qc.invalidateQueries({ queryKey: qk.communities.all });
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}
